import { useCallback, useEffect, useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";

import ReadMoreDocsLink from "components/ReadMoreDocsLink";
import { OPENTOFU_WORKFLOW_TOOL_OPTIONS } from "constants/opentofuWorkflowTools";
import Box from "ds/components/Box";
import FileUpload from "ds/components/FileUpload";
import FormField from "ds/components/Form/Field";
import FormToggleField from "ds/components/Form/ToggleField";
import Input from "ds/components/Input";
import Select from "ds/components/Select";
import SelectInput from "ds/components/Select/Input";
import TooltipModalBody from "ds/components/TooltipModal/Body";
import { TooltipModalTitle } from "ds/components/TooltipModal/Title";
import Typography from "ds/components/Typography";
import useTypedContext from "hooks/useTypedContext";
import { OpenTofuWorkflowTool } from "types/generated";
import { stackValidator } from "utils/formValidators";
import { getDocsUrl } from "utils/getDocsUrl";

import { StackFormContext } from "../../context";
import { StackVendorFormFields } from "../../types";
import useStackCreationAnalyticsVersion from "../../useStackCreationAnalyticsVersion";
import { getSwitchToggleAnalyticsProps, getTooltipAnalyticsProps } from "../../utils";
import VersionInput from "../VersionInput";
import VersionTooltipContent from "../VersionTooltipContent";
import { getValidateToolVersion } from "./getValidateToolVersion";
import useAsyncRangeVersionValidation from "./useAsyncRangeVersionValidation";
import { useFileUpload } from "./useFileUpload";

type NewStackVendorOpenTofuProps = {
  openTofuVersions?: string[];
  reloadVersionsData: () => void;
  reloadLoading: boolean;
};

const NewStackVendorOpenTofu = ({
  openTofuVersions,
  reloadVersionsData,
  reloadLoading,
}: NewStackVendorOpenTofuProps) => {
  // TODO: the getDocsUrl(...) links in this component currently point to Terraform docs.
  // Replace them with OpenTofu-specific docs once available.
  const analyticsVersion = useStackCreationAnalyticsVersion();

  const { setAsyncValidationLoading } = useTypedContext(StackFormContext);
  const fileUploadRef = useRef<HTMLDivElement>(null);

  const { control, watch, trigger, setValue, resetField } = useFormContext<StackVendorFormFields>();

  const handleStateFileChange = useFileUpload();

  const formValues = watch();
  const openTofuValues = formValues.opentofu;

  const handleWorkflowToolChange = useCallback(
    (value: OpenTofuWorkflowTool) => {
      if (openTofuValues.workflowTool === value) {
        return;
      }

      const version = value === OpenTofuWorkflowTool.Opentofu ? openTofuVersions?.[0] || "" : "";

      setValue("opentofu.workflowTool", value);
      setValue("opentofu.version", {
        value: version,
        type: openTofuValues.version.type,
      });

      trigger();
    },
    [openTofuValues.workflowTool, openTofuValues.version.type, setValue, trigger, openTofuVersions]
  );

  const isVersionInputEnabled = openTofuValues.workflowTool !== OpenTofuWorkflowTool.Custom;
  const isStateFileUploadEnabled = openTofuValues.importExistingStateFile;
  const isManageState = openTofuValues.manageState;

  useEffect(() => {
    if (!isManageState) {
      resetField("opentofu.externalStateAccessEnabled");
      resetField("opentofu.importExistingStateFile");
      resetField("opentofu.uploadedStateObjectId");
      resetField("opentofu.uploadedStateFile");
    } else {
      resetField("opentofu.workspace");
    }
  }, [resetField, isManageState]);

  useEffect(() => {
    if (!isStateFileUploadEnabled) {
      resetField("opentofu.uploadedStateObjectId");
      resetField("opentofu.uploadedStateFile");
    }
  }, [resetField, isStateFileUploadEnabled]);

  useEffect(() => {
    if (isStateFileUploadEnabled && fileUploadRef.current) {
      fileUploadRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [isStateFileUploadEnabled]);

  useAsyncRangeVersionValidation(setAsyncValidationLoading);

  return (
    <Box gap="x-large" direction="column">
      <Controller
        name="opentofu.workflowTool"
        control={control}
        render={({ field, fieldState }) => (
          <FormField
            error={fieldState.error?.message}
            noMargin
            label="Workflow tool:"
            {...getTooltipAnalyticsProps("Vendor", "Workflow tool", {
              provider: "OpenTofu",
              version: analyticsVersion,
            })}
            tooltipInfo={
              <>
                <TooltipModalTitle>Workflow tool</TooltipModalTitle>
                <TooltipModalBody align="start">
                  The tool used to execute the workflow commands. This can be OpenTofu or a custom
                  tool. When a custom tool is selected, you must provide a .spacelift/workflow.yml
                  file containing the commands to execute.
                </TooltipModalBody>
              </>
            }
            tooltipInfoVariant="modal"
          >
            {({ ariaInputProps }) => (
              <Select
                value={field.value}
                items={OPENTOFU_WORKFLOW_TOOL_OPTIONS}
                onChange={handleWorkflowToolChange}
                isError={!!fieldState.error}
                {...ariaInputProps}
                renderInput={(input) => <SelectInput {...input} ref={field.ref} />}
              />
            )}
          </FormField>
        )}
      />
      {isVersionInputEnabled && (
        <Controller
          name="opentofu.version"
          control={control}
          rules={{
            validate: getValidateToolVersion(openTofuVersions),
          }}
          render={({ field, fieldState }) => {
            const version = field.value;

            return (
              <VersionInput
                analyticsProps={{
                  location: "Vendor",
                  name: "Version",
                  provider: "OpenTofu",
                  version: analyticsVersion,
                }}
                title="OpenTofu version"
                tooltipInfo={
                  <>
                    <TooltipModalTitle>Choose version</TooltipModalTitle>
                    <TooltipModalBody align="start">
                      <VersionTooltipContent />
                      <ReadMoreDocsLink
                        docsUrl={getDocsUrl(
                          "/concepts/stack/stack-settings#opentofuterraform-specific-settings"
                        )}
                      />
                    </TooltipModalBody>
                  </>
                }
                reloadLoading={reloadLoading}
                reloadVersionsData={reloadVersionsData}
                errorMessage={fieldState.error?.message}
                type={version?.type}
                version={version?.value}
                supportedVersions={openTofuVersions}
                onChange={field.onChange}
                ref={field.ref}
              />
            );
          }}
        />
      )}
      <Box gap="large" direction="column">
        <Controller
          name="opentofu.useSmartSanitization"
          control={control}
          render={({ field }) => (
            <FormToggleField
              variant="switch"
              onChange={field.onChange}
              checked={field.value}
              title="Smart sanitization (recommended)"
              description='If set, Spacelift will attempt to sanitize the resources created by OpenTofu using the information provided by OpenTofu as to which fields are "sensitive" or not'
              {...getSwitchToggleAnalyticsProps("Vendor", "Smart sanitization", {
                provider: "OpenTofu",
                version: analyticsVersion,
              })}
            />
          )}
        />

        <Controller
          name="opentofu.concise"
          control={control}
          render={({ field }) => (
            <FormToggleField
              variant="switch"
              onChange={field.onChange}
              checked={field.value}
              title="Concise output (recommended)"
              description="Suppresses the 'Refreshing state...' progress output during plan, apply and refresh"
              {...getSwitchToggleAnalyticsProps("Vendor", "Concise output", {
                provider: "OpenTofu",
                version: analyticsVersion,
              })}
              tooltipInfo={
                <>
                  <TooltipModalTitle>Concise output</TooltipModalTitle>
                  <TooltipModalBody align="start">
                    When enabled, Spacelift passes -concise to tofu plan/apply/refresh to suppress
                    "Refreshing state..." progress output. Requires OpenTofu 1.7+ at runtime; the
                    flag is silently omitted on older versions.
                  </TooltipModalBody>
                </>
              }
            />
          )}
        />

        <Controller
          name="opentofu.enableCPUProfiling"
          control={control}
          render={({ field }) => (
            <FormToggleField
              variant="switch"
              onChange={field.onChange}
              checked={field.value}
              title="CPU profiling (recommended)"
              description="Captures CPU profiles from OpenTofu init, plan, and apply phases"
              {...getSwitchToggleAnalyticsProps("Vendor", "CPU profiling", {
                provider: "OpenTofu",
                version: analyticsVersion,
              })}
              tooltipInfo={
                <>
                  <TooltipModalTitle>CPU profiling</TooltipModalTitle>
                  <TooltipModalBody align="start">
                    When enabled, Spacelift captures CPU profiles from OpenTofu init, plan, and
                    apply phases. Requires OpenTofu 1.10+ at runtime; silently omitted on older
                    versions.
                  </TooltipModalBody>
                </>
              }
            />
          )}
        />

        <Controller
          name="opentofu.manageState"
          control={control}
          render={({ field }) => (
            <FormToggleField
              variant="switch"
              onChange={field.onChange}
              checked={field.value}
              title="Manage state (recommended)"
              description="Indicates whether Spacelift should take care of the OpenTofu state"
              {...getSwitchToggleAnalyticsProps("Vendor", "Manage state", {
                provider: "OpenTofu",
                version: analyticsVersion,
              })}
              tooltipInfo={
                <>
                  <TooltipModalTitle>State management</TooltipModalTitle>
                  <TooltipModalBody align="start">
                    <Typography tag="p" variant="p-body3">
                      For those of you who don't want to manage OpenTofu state, Spacelift offers an
                      optional sophisticated state backend synchronized with the rest of the
                      application to maximize security and convenience.
                    </Typography>
                    <ReadMoreDocsLink docsUrl={getDocsUrl("/vendors/terraform/state-management")} />
                  </TooltipModalBody>
                </>
              }
            />
          )}
        />
        {!isManageState && (
          <Controller
            name="opentofu.workspace"
            control={control}
            rules={{ validate: (value) => stackValidator.validateWorkspaceField(value) || true }}
            render={({ field, fieldState }) => (
              <FormField label="Workspace:" error={fieldState.error?.message} isOptional noMargin>
                {({ ariaInputProps }) => (
                  <Input
                    error={!!fieldState.error}
                    type="text"
                    value={field.value}
                    placeholder="OpenTofu workspace to select"
                    onChange={field.onChange}
                    {...ariaInputProps}
                  />
                )}
              </FormField>
            )}
          />
        )}

        {isManageState && (
          <>
            <Controller
              name="opentofu.externalStateAccessEnabled"
              control={control}
              render={({ field }) => (
                <FormToggleField
                  variant="switch"
                  onChange={field.onChange}
                  checked={field.value}
                  title="External state access"
                  description="Enables external, read-only access to the state of this Stack"
                  tooltipInfo={
                    <>
                      <TooltipModalTitle>External state access</TooltipModalTitle>
                      <TooltipModalBody align="start">
                        Only stacks with Space writer role or users with write permission to this
                        stack's space can access the state.
                        <ReadMoreDocsLink
                          docsUrl={getDocsUrl("/vendors/terraform/external-state-access")}
                        />
                      </TooltipModalBody>
                    </>
                  }
                  {...getSwitchToggleAnalyticsProps("Vendor", "External state access enabled", {
                    provider: "OpenTofu",
                    version: analyticsVersion,
                  })}
                />
              )}
            />

            <Box direction="column" gap="large">
              <Controller
                name="opentofu.importExistingStateFile"
                control={control}
                render={({ field }) => (
                  <FormToggleField
                    variant="switch"
                    onChange={field.onChange}
                    checked={field.value}
                    title="Import existing state file"
                    description="Spacelift will create a state file for you the first time you apply changes"
                    {...getSwitchToggleAnalyticsProps("Vendor", "Import existing state file", {
                      provider: "OpenTofu",
                      version: analyticsVersion,
                    })}
                    tooltipInfo={
                      <>
                        <TooltipModalTitle>Import state file</TooltipModalTitle>
                        <TooltipModalBody align="start">
                          <Typography tag="p" variant="p-body3">
                            This is convenient if you have no existing resources to import. On the
                            other hand, if you're importing an existing OpenTofu project, you may
                            want to import its state file now to avoid having to import individual
                            resources later.
                          </Typography>
                          <ReadMoreDocsLink
                            docsUrl={getDocsUrl(
                              "/vendors/terraform/state-management#importing-existing-state-file-into-your-terraform-stacks"
                            )}
                          />
                        </TooltipModalBody>
                      </>
                    }
                  />
                )}
              />

              {isStateFileUploadEnabled && (
                <div ref={fileUploadRef}>
                  <Controller
                    name="opentofu.uploadedStateFile"
                    control={control}
                    rules={{ required: "State file is required." }}
                    render={({ fieldState, field }) => (
                      <FormField
                        error={fieldState.error?.message}
                        label="State file upload"
                        noMargin
                      >
                        <FileUpload
                          ariaLabel="Upload state file"
                          caption="Drag and drop state file file here"
                          file={field.value}
                          onChange={handleStateFileChange}
                        />
                      </FormField>
                    )}
                  />
                </div>
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default NewStackVendorOpenTofu;
